import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Tags, Receipt, Wallet } from 'lucide-react';
import {
  useAccountingBundle,
  addCategory,
  deleteCategory,
  addExpense,
  deleteExpense,
  type PaymentMethod,
} from '../lib/accountingStore';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, filterByRange, formatRangeLabel } from '../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { DashboardCard } from './dashboard/DashboardCard';
import { useCanWrite } from '../lib/permissions';
import { useTranslation } from '../lib/i18n';
import { defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../lib/workspaceAsOf';

const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

interface ExpensesProps {
  setActiveItem?: (id: string) => void;
}

export function Expenses(_props: ExpensesProps) {
  const { t } = useTranslation();
  const [bundle, update] = useAccountingBundle();
  const canWrite = useCanWrite('expenses');
  const dr = useDateRange('this_month');
  const [tab, setTab] = useState<'record' | 'categories'>('record');

  // Record-expense form
  const [form, setForm] = useState({
    category: '',
    amount: '',
    expenseDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate(),
    paymentMethod: 'cash' as PaymentMethod,
    description: '',
    paidBy: '',
    vendor: '',
  });
  const [newCat, setNewCat] = useState('');

  const filtered = useMemo(
    () => filterByRange(bundle.expenses, dr.range, (e) => e.expenseDate),
    [bundle.expenses, dr.range]
  );

  const totalSpend = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const byCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of filtered) m.set(e.category, (m.get(e.category) || 0) + (e.amount || 0));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const submitExpense = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    const category = form.category.trim();
    if (!category || !form.amount) return;
    update((b) =>
      addExpense(b, {
        category,
        amount: Number(form.amount),
        expenseDate: new Date(form.expenseDate).toISOString(),
        paymentMethod: form.paymentMethod,
        description: form.description,
        paidBy: form.paidBy,
        vendor: form.vendor,
      })
    );
    setForm((f) => ({ ...f, amount: '', description: '', vendor: '' }));
  };

  const exportRows: ExportRow[] = filtered.map((e) => ({
    Date: fmtDate(e.expenseDate),
    Category: e.category,
    SubCategory: e.subCategory || '',
    Description: e.description || '',
    Vendor: e.vendor || '',
    PaymentMethod: e.paymentMethod || '',
    PaidBy: e.paidBy || '',
    Amount: e.amount,
  }));

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h2 className="app-page-title">{t('expenses.title')}</h2>
          <p className="app-page-sub">
            {t('expenses.subtitle', { range: formatRangeLabel(dr.range) })}
          </p>
        </div>
        <ExportMenu title={`${t('expenses.expenseReport')} (${formatRangeLabel(dr.range)})`} excelFilename="expenses.xls" rows={exportRows} />
      </div>

      <div className="mb-5">
        <DateRangeFilter
          start={dr.range.start}
          end={dr.range.end}
          preset={dr.preset}
          onStart={dr.setStart}
          onEnd={dr.setEnd}
          onPreset={dr.setPreset}
          onClear={dr.clear}
        />
      </div>

      {/* Summary */}
      <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-2.5">
        <DashboardCard title={t('expenses.totalSpend')} value={inr(totalSpend)} icon={Wallet} accentTop="border-l-[3px] border-l-amber-500" iconColor="text-amber-700" onClick={() => {}} />
        <DashboardCard title={t('common.records')} value={String(filtered.length)} icon={Receipt} accentTop="border-l-[3px] border-l-zinc-400" iconColor="text-zinc-600" onClick={() => {}} />
        <DashboardCard title={t('expenses.categories')} value={String(byCategory.length)} icon={Tags} accentTop="border-l-[3px] border-l-emerald-600" iconColor="text-emerald-700" onClick={() => {}} />
      </div>

      {/* Tabs */}
      <div className="mb-4 inline-flex rounded-lg border border-zinc-200 bg-white p-1">
        {(['record', 'categories'] as const).map((tabKey) => (
          <button
            key={tabKey}
            type="button"
            onClick={() => setTab(tabKey)}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold capitalize transition ${
              tab === tabKey ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
            }`}
          >
            {tabKey === 'record' ? t('expenses.title') : t('expenses.categories')}
          </button>
        ))}
      </div>

      {tab === 'record' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Form */}
          {canWrite && (
          <form onSubmit={submitExpense} className="app-card app-card-inner lg:col-span-1">
            <h3 className="mb-3 text-sm font-semibold text-zinc-900">{t('expenses.recordExpense')}</h3>
            <div className="space-y-3">
              <div>
                <label className="app-label">{t('expenses.category')}</label>
                <select
                  className="app-field"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  disabled={bundle.categories.length === 0}
                  required
                >
                  {bundle.categories.length === 0 ? (
                    <option value="">No categories — add one first</option>
                  ) : (
                    <>
                      <option value="">{t('common.select')}</option>
                      {bundle.categories.map((c) => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {bundle.categories.length === 0 ? (
                  <p className="mt-1.5 text-xs text-amber-700">
                    Open the Categories tab to add your first expense category.
                  </p>
                ) : null}
              </div>
              <div>
                <label className="app-label">{t('common.amount')} (₹)</label>
                <input
                  type="number"
                  min="0"
                  className="app-field"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="app-label">{t('common.date')}</label>
                  <input
                    type="date"
                    className="app-field w-full"
                    value={form.expenseDate}
                    onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="app-label">{t('employees.method')}</label>
                  <select
                    className="app-field w-full"
                    value={form.paymentMethod}
                    onChange={(e) => setForm({ ...form, paymentMethod: e.target.value as PaymentMethod })}
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="app-label">{t('expenses.description')}</label>
                <input
                  className="app-field"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What was this for?"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="app-label">{t('expenses.vendor')}</label>
                  <input className="app-field w-full" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
                </div>
                <div>
                  <label className="app-label">{t('expenses.paidBy')}</label>
                  <input className="app-field w-full" value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="app-btn-primary w-full" disabled={bundle.categories.length === 0}>
                <Plus className="mr-1.5 h-4 w-4" /> {t('common.add')}
              </button>
            </div>
          </form>
          )}

          {/* List */}
          <div className="lg:col-span-2">
            <div className="app-table-wrap">
              <table className="app-table">
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th>{t('expenses.category')}</th>
                    <th>{t('expenses.description')}</th>
                    <th className="text-right">{t('common.amount')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm text-zinc-500">
                        No expenses in this period.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((e) => (
                      <tr key={e.id}>
                        <td className="whitespace-nowrap">{fmtDate(e.expenseDate)}</td>
                        <td>
                          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
                            {e.category}
                          </span>
                          {e.subCategory ? <span className="ml-1 text-xs text-zinc-400">{e.subCategory}</span> : null}
                        </td>
                        <td className="max-w-[260px] truncate text-zinc-600">{e.description || '—'}</td>
                        <td className="whitespace-nowrap text-right font-semibold text-zinc-900">{inr(e.amount)}</td>
                        <td className="text-right">
                          {canWrite && (
                            <button
                              type="button"
                              onClick={() => update((b) => deleteExpense(b, e.id))}
                              className="rounded p-1 text-zinc-400 hover:bg-rose-50 hover:text-rose-600"
                              aria-label="Delete expense"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {byCategory.length > 0 && (
              <div className="app-card app-card-inner mt-4">
                <h3 className="mb-3 text-sm font-semibold text-zinc-900">{t('expenses.spendByCategory')}</h3>
                <div className="space-y-2">
                  {byCategory.map(([cat, amt]) => (
                    <div key={cat}>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="font-medium text-zinc-700">{cat}</span>
                        <span className="font-semibold text-zinc-900">{inr(amt)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
                        <div
                          className="h-full rounded-full bg-emerald-500"
                          style={{ width: `${totalSpend ? (amt / totalSpend) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {canWrite && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!canWrite) return;
              if (!newCat.trim()) return;
              update((b) => addCategory(b, { name: newCat }));
              setNewCat('');
            }}
            className="app-card app-card-inner lg:col-span-1"
          >
            <h3 className="mb-3 text-sm font-semibold text-zinc-900">{t('expenses.addCategory')}</h3>
            <label className="app-label">{t('expenses.category')}</label>
            <input className="app-field mb-3" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Fuel" />
            <button type="submit" className="app-btn-primary w-full">
              <Plus className="mr-1.5 h-4 w-4" /> {t('expenses.addCategory')}
            </button>
          </form>
          )}

          <div className="lg:col-span-2">
            <div className="app-table-wrap">
              <table className="app-table">
                <thead>
                  <tr>
                    <th>{t('expenses.category')}</th>
                    <th>{t('customer.kind')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {bundle.categories.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-10 text-center text-sm text-zinc-500">
                        No categories yet. Add your first category using the form.
                      </td>
                    </tr>
                  ) : (
                    bundle.categories.map((c) => (
                    <tr key={c.id}>
                      <td className="font-medium text-zinc-900">{c.name}</td>
                      <td className="text-zinc-500">{c.expenseType === 'recurring' ? 'Recurring' : 'One-time'}</td>
                      <td className="text-right">
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => update((b) => deleteCategory(b, c.id))}
                            className="rounded p-1 text-zinc-400 hover:bg-rose-50 hover:text-rose-600"
                            aria-label="Delete category"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
